import Button from '../../Button';
import { FeLink } from '../../FeLink';
import { AioModuleTracking, sendAioNavigate } from '../tracking';

type CtaAioModuleProps = {
  description?: string;
  /** Already trimmed and known non-empty — see the note below. */
  label: string;
  url: string;
} & AioModuleTracking;

/**
 * Bottom CTA of the AIO module (CMS-1937) — a line of copy on the left and an
 * outlined button on the right, separated from the sections above by the
 * card's only footer rule.
 *
 * Both texts are editor fields, so there is no i18n here.
 *
 * Unlike the other sections this one does **not** gate itself. Its divider has
 * to span the full card width, so it lives outside the padded body and the
 * card's bottom border is drawn by the wrapper around it — border and row have
 * to appear and disappear together, which only the parent can decide.
 *
 * No `target="_blank"`: unlike the banner and the product CTAs, this one
 * points at our own campaign page in the ticket's example, and the schema
 * allows a root-relative path.
 */
export const CtaAioModule = ({
  description,
  label,
  url,
  ...tracking
}: CtaAioModuleProps): JSX.Element => (
  <div
    data-component="aio-module-cta"
    className="flex items-center gap-spacing/gap/x-sm border-t border-borders/default py-spacing/gap/section-y px-spacing/padding/x"
  >
    {description?.trim() && (
      <p className="mhg-alpha-typography-body-2 text-text/secondary-lighter">
        {description.trim()}
      </p>
    )}

    {/* `ml-auto` rather than `justify-between` on the row: the description
        is optional, and with one child `justify-between` is flex-start, which
        would drop the button to the left edge. */}
    <FeLink
      href={url}
      className="ml-auto shrink-0"
      onClick={() =>
        // #2540. The module has one footer button, so `index` is always 0.
        sendAioNavigate(tracking, {
          componentType: 'button',
          title: `Footer_${label}`,
          action: 'click',
          index: 0,
        })
      }
    >
      <Button size="small" variant="outline" styleType="primary">
        {label}
      </Button>
    </FeLink>
  </div>
);
